import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    HiOutlineHome,
    HiOutlineBriefcase,
    HiOutlineSearch,
    HiOutlineChatAlt2,
    HiOutlineCog,
    HiOutlineArrowLeft,
    HiOutlineCheck,
    HiOutlineCamera,
    HiOutlineCurrencyDollar,
    HiOutlineCalendar,
    HiOutlineDocumentText,
    HiOutlineUsers,
    HiOutlineGlobeAlt,
    HiOutlineCloudUpload,
    HiPlus
} from "react-icons/hi";
import { locations } from "../../data/locations";

const CreateCampaign = () => {
    const navigate = useNavigate();
    const [step, setStep] = useState(1);

    // Updated State for 5-step flow
    const [formData, setFormData] = useState({
        // Step 1: Platform & Types
        platform: "Instagram",
        creatorType: "UGC", // UGC, Influencer
        campaignType: "Reel", // Reel, Static Post, Story

        // Step 2: Product Details
        productName: "",
        productLink: "",
        productDescription: "",
        productImage: null, // File object

        // Step 4: Creator Requirements
        gender: "Any",
        niches: [],
        minFollowers: "",
        maxFollowers: "",
        creatorLocation: "",

        // Step 5: Budget & Logistics
        numberOfCreators: "",
        budgetPerCreator: "",
        totalBudget: "",
        startDate: "",
        deadline: "",
    });

    const [errors, setErrors] = useState({});
    const [imagePreview, setImagePreview] = useState(null);
    const totalSteps = 4;

    const validateStep = () => {
        const newErrors = {};
        if (step === 2) {
            if (!formData.productName.trim()) newErrors.productName = "Product Name is required";
            if (!formData.productLink.trim()) newErrors.productLink = "Product Link is required";
            if (!formData.productDescription.trim()) newErrors.productDescription = "Description is required";
        }
        if (step === 4) {
            if (!formData.numberOfCreators) newErrors.numberOfCreators = "Number of creators is required";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleNext = () => {
        if (validateStep()) {
            setStep(prev => prev + 1);
            window.scrollTo(0, 0);
        }
    };

    const handleBack = () => {
        if (step > 1) {
            setStep(prev => prev - 1);
            window.scrollTo(0, 0);
        } else {
            navigate('/brand/dashboard');
        }
    };

    const handleSubmit = () => {
        if (validateStep()) {
            console.log("Submitting Campaign:", formData);
            navigate('/brand/campaigns');
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        // Clear error when user types
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    const handleImageChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            setFormData(prev => ({ ...prev, productImage: file }));
            setImagePreview(URL.createObjectURL(file));
        }
    };

    const renderStep = () => {
        switch (step) {
            case 1: return <StepTypePlatform data={formData} setFormData={setFormData} />;
            case 2: return <StepProductDetails data={formData} onChange={handleChange} onImageChange={handleImageChange} imagePreview={imagePreview} errors={errors} />;
            case 3: return <StepCreatorReqs data={formData} onChange={handleChange} />;
            case 4: return <StepBudgetTimeline data={formData} onChange={handleChange} errors={errors} />;
            default: return <StepTypePlatform data={formData} setFormData={setFormData} />;
        }
    };

    return (
        <div className="flex h-screen bg-[#FDFBF7]">
            {/* Sidebar */}
            <aside className="w-64 bg-white border-r border-gray-100 flex flex-col hidden md:flex">
                <div className="p-6 flex items-center gap-2 text-black font-bold text-xl">
                    <img src="/logo.png" alt="Braccet" className="w-8 h-8 object-contain" />
                    <span>Braccet</span>
                </div>
                <nav className="flex-1 px-4 py-6 space-y-2">
                    <NavItem to="/brand/dashboard" icon={<HiOutlineHome />} label="Overview" />
                    <NavItem to="/brand/campaigns" icon={<HiOutlineBriefcase />} label="Campaigns" active />
                    <NavItem to="/brand/search" icon={<HiOutlineSearch />} label="Find Creators" />
                    <NavItem to="/brand/messages" icon={<HiOutlineChatAlt2 />} label="Messages" />
                    <NavItem to="/brand/settings" icon={<HiOutlineCog />} label="Settings" />
                </nav>
            </aside>

            {/* Main Content */}
            <main className="flex-1 overflow-hidden flex flex-col h-screen">
                {/* Header */}
                <header className="bg-white/80 backdrop-blur-md border-b border-gray-100 px-6 py-4 flex items-center gap-4 shrink-0 justify-between">
                    <div className="flex items-center gap-4">
                        <button onClick={handleBack} className="p-2 hover:bg-gray-100 rounded-full text-gray-500 transition-colors">
                            <HiOutlineArrowLeft className="text-xl" />
                        </button>
                        <div>
                            <h1 className="text-xl font-bold text-gray-900">Create New Campaign</h1>
                            <p className="text-xs text-gray-500">Step {step} of {totalSteps}</p>
                        </div>
                    </div>
                </header>

                <div className="flex-1 overflow-y-auto p-6">
                    <div className="max-w-3xl mx-auto">
                        {/* Progress Bar */}
                        <div className="w-full bg-gray-200 h-1.5 rounded-full mb-8">
                            <div
                                className="bg-[#0044CC] h-1.5 rounded-full transition-all duration-300"
                                style={{ width: `${(step / totalSteps) * 100}%` }}
                            ></div>
                        </div>

                        {/* Step Content */}
                        <div className="bg-white p-8 rounded-3xl shadow-sm border border-gray-100 min-h-[500px] mb-6">
                            {renderStep()}
                        </div>

                        {/* Navigation Actions */}
                        <div className="flex justify-between items-center pb-10">
                            <button
                                onClick={handleBack}
                                className={`px-6 py-3 rounded-xl font-bold transition-colors text-gray-600 hover:bg-gray-100`}
                            >
                                Back
                            </button>

                            {step < totalSteps ? (
                                <button
                                    onClick={handleNext}
                                    className="px-10 py-3 bg-[#0044CC] text-white rounded-xl font-bold hover:bg-blue-700 transition-colors shadow-lg shadow-blue-200"
                                >
                                    Continue
                                </button>
                            ) : (
                                <button
                                    onClick={handleSubmit}
                                    className="px-10 py-3 bg-green-600 text-white rounded-xl font-bold hover:bg-green-700 transition-colors shadow-lg shadow-green-200 flex items-center gap-2"
                                >
                                    Publish Campaign <HiOutlineCheck />
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
};

// --- Step Components ---

const StepTypePlatform = ({ data, setFormData }) => (
    <div className="space-y-8 animate-fadeIn">
        <h2 className="text-2xl font-bold text-gray-900">Platform</h2>
        <div className="flex gap-4">
            <div className="px-6 py-3 rounded-xl border-2 border-[#0044CC] bg-blue-50 text-[#0044CC] font-bold flex items-center gap-2">
                <i className="fa-brands fa-instagram text-xl"></i> Instagram
            </div>
            <p className="self-center text-sm text-gray-500 italic">Only Instagram campaigns are supported currently.</p>
        </div>

        {/* Creator Type Selection */}
        <h2 className="text-2xl font-bold text-gray-900 mt-8">What type of creator are you looking for?</h2>
        <div className="flex gap-4">
            {['UGC', 'Influencer'].map(type => (
                <div
                    key={type}
                    onClick={() => setFormData(prev => ({ ...prev, creatorType: type }))}
                    className={`flex-1 p-6 rounded-xl border-2 cursor-pointer transition-all flex flex-col items-center justify-center gap-2
                        ${data.creatorType === type ? 'border-[#0044CC] bg-blue-50' : 'border-gray-100 hover:border-gray-300'}
                    `}
                >
                    <div className={`text-2xl ${data.creatorType === type ? 'text-[#0044CC]' : 'text-gray-400'}`}>
                        {type === 'UGC' ? <HiOutlineCamera /> : <HiOutlineUsers />}
                    </div>
                    <span className={`font-bold text-lg ${data.creatorType === type ? 'text-[#0044CC]' : 'text-gray-700'}`}>{type}</span>
                    <p className="text-xs text-center text-gray-500">
                        {type === 'UGC' ? "Content for your brand channels." : "Promotion on creator channels."}
                    </p>
                </div>
            ))}
        </div>

        <h2 className="text-2xl font-bold text-gray-900 mt-8">What type of content do you need?</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {['Reel', 'Static Post', 'Story'].map(type => (
                <div
                    key={type}
                    onClick={() => setFormData(prev => ({ ...prev, campaignType: type }))}
                    className={`p-6 rounded-xl border-2 cursor-pointer transition-all hover:shadow-md
                        ${data.campaignType === type ? 'border-[#0044CC] bg-blue-50' : 'border-gray-100 hover:border-gray-300'}
                    `}
                >
                    <div className={`w-10 h-10 rounded-full mb-3 flex items-center justify-center text-xl
                        ${data.campaignType === type ? 'bg-[#0044CC] text-white' : 'bg-gray-100 text-gray-500'}
                    `}>
                        {type === 'Reel' && <i className="fa-solid fa-clapperboard"></i>}
                        {type === 'Static Post' && <i className="fa-regular fa-image"></i>}
                        {type === 'Story' && <i className="fa-solid fa-circle-notch"></i>}
                    </div>
                    <h3 className={`font-bold text-lg mb-1 ${data.campaignType === type ? 'text-[#0044CC]' : 'text-gray-900'}`}>{type}</h3>
                </div>
            ))}
        </div>
    </div>
);


const StepProductDetails = ({ data, onChange, onImageChange, imagePreview, errors }) => (
    <div className="space-y-6 animate-fadeIn">
        <h2 className="text-2xl font-bold text-gray-900">Product Details</h2>
        <Input label="Product Name" name="productName" value={data.productName} onChange={onChange} placeholder="e.g. Summer Glow Serum" error={errors?.productName} />
        <Input label="Product Link" name="productLink" value={data.productLink} onChange={onChange} placeholder="https://yourstore.com/product" error={errors?.productLink} />

        <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">Description</label>
            <textarea
                name="productDescription"
                value={data.productDescription}
                onChange={onChange}
                rows={4}
                className={`w-full px-4 py-3 bg-gray-50 border rounded-xl focus:outline-none focus:ring-2 transition-shadow resize-none
                    ${errors?.productDescription ? 'border-red-500 ring-red-500/20' : 'border-gray-200 focus:ring-blue-500'}
                `}
                placeholder="Key features, benefits, selling points..."
            />
            {errors?.productDescription && <p className="text-xs text-red-500 mt-1 font-medium">{errors.productDescription}</p>}
        </div>

        <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">Product Image / Media</label>
            <div className={`border-2 border-dashed border-gray-200 rounded-2xl h-48 flex flex-col items-center justify-center text-gray-400 hover:border-blue-300 hover:bg-blue-50 transition-colors cursor-pointer bg-gray-50 relative overflow-hidden group`}>

                {imagePreview ? (
                    <img src={imagePreview} alt="Product" className="w-full h-full object-contain p-2" />
                ) : (
                    <>
                        <HiOutlineCloudUpload className="text-4xl mb-2" />
                        <span className="font-medium">Upload Product Image</span>
                        <span className="text-xs mt-1">PNG, JPG recommended</span>
                    </>
                )}

                <input
                    type="file"
                    accept="image/*"
                    onChange={onImageChange}
                    className="absolute inset-0 opacity-0 cursor-pointer"
                />
            </div>
        </div>
    </div>
);

const StepCreatorReqs = ({ data, onChange }) => {
    const niches = ["Fashion", "Beauty", "Lifestyle", "Travel", "Fitness", "Food", "Tech", "Gaming", "Parenting", "Business"];

    const toggleNiche = (niche) => {
        const current = data.niches || [];
        if (current.includes(niche)) {
            onChange({ target: { name: 'niches', value: current.filter(n => n !== niche) } });
        } else {
            if (current.length < 3) {
                onChange({ target: { name: 'niches', value: [...current, niche] } });
            }
        }
    };

    // Helper to handle slider changes
    const handleRangeChange = (min, max) => {
        onChange({ target: { name: 'minFollowers', value: min } });
        onChange({ target: { name: 'maxFollowers', value: max } });
    };

    return (
        <div className="space-y-8 animate-fadeIn">
            <h2 className="text-2xl font-bold text-gray-900">Creator Requirements</h2>

            {/* Gender Selection */}
            <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Gender</label>
                <div className="flex gap-4">
                    {['Any', 'Female', 'Male'].map(g => (
                        <div
                            key={g}
                            onClick={() => onChange({ target: { name: 'gender', value: g } })}
                            className={`px-6 py-3 rounded-xl border-2 cursor-pointer font-bold transition-all
                                ${data.gender === g ? 'border-[#0044CC] bg-blue-50 text-[#0044CC]' : 'border-gray-100 text-gray-600 hover:border-gray-300'}
                            `}
                        >
                            {g}
                        </div>
                    ))}
                </div>
            </div>

            {/* Niche Selection (Max 3) */}
            <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                    Niche <span className="text-gray-400 font-normal ml-2">(Select up to 3)</span>
                </label>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    {niches.map(n => (
                        <div
                            key={n}
                            onClick={() => toggleNiche(n)}
                            className={`px-3 py-2 rounded-lg border text-sm font-medium cursor-pointer transition-colors text-center
                                ${(data.niches || []).includes(n)
                                    ? 'bg-[#0044CC] text-white border-[#0044CC]'
                                    : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'}
                            `}
                        >
                            {n}
                        </div>
                    ))}
                </div>
            </div>

            {/* Follower Range Slider */}
            <div>
                <label className="block text-sm font-bold text-gray-700 mb-4">Follower Count Range</label>
                <div className="px-2">
                    <MultiRangeSlider
                        min={1000}
                        max={500000}
                        onChange={({ min, max }) => handleRangeChange(min, max)}
                    />
                    <div className="flex justify-between text-sm font-medium text-gray-500 mt-2">
                        <span>{(data.minFollowers || 1000).toLocaleString()}</span>
                        <span>{(data.maxFollowers || 500000).toLocaleString()}+</span>
                    </div>
                </div>
            </div>

            {/* Location Dropdown */}
            <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Location</label>
                <div className="relative">
                    <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg">
                        <HiOutlineGlobeAlt />
                    </div>
                    <select
                        name="creatorLocation"
                        value={data.creatorLocation}
                        onChange={onChange}
                        className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 transition-shadow appearance-none cursor-pointer text-gray-700 font-medium"
                    >
                        <option value="">Select Location</option>
                        {locations.map((loc, index) => (
                            <option key={index} value={loc}>{loc}</option>
                        ))}
                    </select>
                    <div className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                        <i className="fa-solid fa-chevron-down text-xs"></i>
                    </div>
                </div>
            </div>
        </div>
    );
};


const StepBudgetTimeline = ({ data, onChange, errors }) => (
    <div className="space-y-6 animate-fadeIn">
        <h2 className="text-2xl font-bold text-gray-900">Budget & Timeline</h2>

        <div className="grid grid-cols-2 gap-6">
            <Input label="Budget per Creator" name="budgetPerCreator" value={data.budgetPerCreator} onChange={onChange} icon={<HiOutlineCurrencyDollar />} placeholder="200.00" />
            <Input label="Number of Creators" name="numberOfCreators" type="number" value={data.numberOfCreators} onChange={onChange} icon={<i className="fa-solid fa-users"></i>} placeholder="e.g. 5" error={errors?.numberOfCreators} />
        </div>

        <Input label="Total Budget (Estimated)" name="totalBudget" value={data.totalBudget} onChange={onChange} icon={<HiOutlineCurrencyDollar />} placeholder="1000.00" />

        <div className="grid grid-cols-2 gap-6">
            <Input label="Start Date" name="startDate" type="date" value={data.startDate} onChange={onChange} />
            <Input label="Deadline" name="deadline" type="date" value={data.deadline} onChange={onChange} />
        </div>

        <div className="bg-green-50 p-6 rounded-2xl border border-green-100 mt-4">
            <h4 className="font-bold text-green-800 mb-2">Ready to Launch?</h4>
            <p className="text-sm text-green-700">
                Your campaign will be live immediately after publishing. Creators matching your requirements will be notified.
            </p>
        </div>
    </div>
);


const Input = ({ label, name, value, onChange, placeholder, type = "text", icon, error }) => (
    <div className="w-full">
        <label className="block text-sm font-bold text-gray-700 mb-2">{label}</label>
        <div className="relative">
            {icon && <div className={`absolute left-4 top-1/2 -translate-y-1/2 text-lg ${error ? 'text-red-400' : 'text-gray-400'}`}>{icon}</div>}
            <input
                type={type}
                name={name}
                value={value}
                onChange={onChange}
                placeholder={placeholder}
                className={`w-full ${icon ? 'pl-12' : 'px-4'} py-3 bg-gray-50 border rounded-xl focus:outline-none focus:ring-2 transition-shadow font-medium
                    ${error ? 'border-red-500 focus:ring-red-500/20 text-red-600 placeholder:text-red-300' : 'border-gray-200 focus:ring-blue-500'}
                `}
            />
        </div>
        {error && <p className="text-xs text-red-500 mt-1 font-medium">{error}</p>}
    </div>
);

const NavItem = ({ to, icon, label, active }) => (
    <Link to={to} className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${active ? 'bg-blue-50 text-[#0044CC]' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'}`}>
        <span className="text-xl">{icon}</span>
        {label}
    </Link>
);


// Custom MultiRange Slider Component
const MultiRangeSlider = ({ min, max, onChange }) => {
    const [minVal, setMinVal] = useState(min);
    const [maxVal, setMaxVal] = useState(max);
    const minValRef = React.useRef(min);
    const maxValRef = React.useRef(max);
    const range = React.useRef(null);

    // Convert to percentage
    const getPercent = React.useCallback(
        (value) => Math.round(((value - min) / (max - min)) * 100),
        [min, max]
    );

    // Set width of the range to decrease from the left side
    React.useEffect(() => {
        const minPercent = getPercent(minVal);
        const maxPercent = getPercent(maxValRef.current);

        if (range.current) {
            range.current.style.left = `${minPercent}%`;
            range.current.style.width = `${maxPercent - minPercent}%`;
        }
    }, [minVal, getPercent]);

    // Set width of the range to decrease from the right side
    React.useEffect(() => {
        const minPercent = getPercent(minValRef.current);
        const maxPercent = getPercent(maxVal);

        if (range.current) {
            range.current.style.width = `${maxPercent - minPercent}%`;
        }
    }, [maxVal, getPercent]);

    return (
        <div className="relative w-full h-12 flex items-center">
            <input
                type="range"
                min={min}
                max={max}
                value={minVal}
                onChange={(event) => {
                    const value = Math.min(Number(event.target.value), maxVal - 1);
                    setMinVal(value);
                    minValRef.current = value;
                    onChange({ min: value, max: maxVal });
                }}
                className="thumb thumb--left pointer-events-none absolute h-0 w-full outline-none z-[3]"
                style={{ zIndex: minVal > max - 100 && "5" }}
            />
            <input
                type="range"
                min={min}
                max={max}
                value={maxVal}
                onChange={(event) => {
                    const value = Math.max(Number(event.target.value), minVal + 1);
                    setMaxVal(value);
                    maxValRef.current = value;
                    onChange({ min: minVal, max: value });
                }}
                className="thumb thumb--right pointer-events-none absolute h-0 w-full outline-none z-[4]"
            />

            <div className="relative w-full">
                <div className="slider__track absolute rounded h-2 w-full bg-gray-200 z-[1]" />
                <div ref={range} className="slider__range absolute rounded h-2 bg-[#0044CC] z-[2]" />
            </div>

            {/* Custom Styles for Slider Thumbs (since we can't use external CSS easily here) */}
            <style>{`
                .thumb::-webkit-slider-thumb {
                    -webkit-appearance: none;
                    -webkit-tap-highlight-color: transparent;
                    pointer-events: auto;
                    height: 24px;
                    width: 24px;
                    border-radius: 50%;
                    background-color: #fff;
                    border: 2px solid #0044CC;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.3);
                    cursor: pointer;
                    margin-top: -10px; /* shift up to center on track */
                }
                .thumb::-moz-range-thumb {
                    -webkit-appearance: none;
                    pointer-events: auto;
                    height: 24px;
                    width: 24px;
                    border-radius: 50%;
                    background-color: #fff;
                    border: 2px solid #0044CC;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.3);
                    cursor: pointer;
                    border: none;
                }
            `}</style>
        </div>
    );
};

export default CreateCampaign;
